import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import RedirectAction from 'yoda-app-shell/lib/action/RedirectAction';
import IrisPage from 'yoda-site-components/lib/components/IrisPage/IrisPage';
import IrisActions from 'yoda-site-components/lib/actions/IrisAction';
import irisComponentsForMarketing from 'yoda-site-components/lib/iris/components/forMarketing';
import Layout from 'yoda-site-components/lib/components/Layout/Layout';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import ProductConstants from 'yoda-site-components/lib/common/ProductConstants';
import renderMiniPDP from '../../../helper/renderMiniPDP';
import marketingAction from '../../../actions/MarketingAction';
import FeatureShops from './FeatureShops';

/* istanbul ignore next */
function Marketing({
    actions,
    irisData,
    location,
    history,
    irisFallbackPreferences,
    irisFallbackEnabled,
    disableIrisCall,
    isPreview,
    enableFeatureShops,
    enableMiniPDPFeature,
}) {
    const [featureShopPageName, setFeatureShopPageName] = useState('');
    const prevIrisDataRef = useRef(irisData);

    const params = location.pathname;
    const featureShopParam = params?.includes(ProductConstants.FEATURE_SHOP);
    const isFeatureShopEligible = enableFeatureShops && featureShopParam;

    // Analytics trigger
    const triggerAnalyticsData = useCallback(() => {
        if (!__SERVER__ && !isEmpty(irisData)) {
            const { analytics } = irisData;
            if (analytics) {
                actions.getMarketingLoaded(analytics.pageName, analytics.pageType);
            }
        } else if (isFeatureShopEligible) {
            const featureShopSlug = params?.split(ProductConstants.FEATURE_SHOP);
            const pageType = 'promotions';
            const pageName = `featureshop:${featureShopSlug && featureShopSlug[1]}:${featureShopPageName}`;
            actions.getMarketingLoaded(pageName, pageType);
        }
    }, [irisData, isFeatureShopEligible, featureShopPageName, params, actions]);

    // Mount: trigger analytics + set up global triggerShippingHandling
    useEffect(() => {
        triggerAnalyticsData();

        window.triggerShippingHandling = () => {
            actions.getIrisTemplateData(
                params,
                true,
                undefined,
                irisFallbackEnabled,
                irisFallbackPreferences,
                RedirectAction.setRedirectUrl,
                disableIrisCall
            );
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Update: fire analytics when irisData or featureShopPageName changes
    useEffect(() => {
        if (prevIrisDataRef.current !== irisData || featureShopPageName) {
            triggerAnalyticsData();
        }
        prevIrisDataRef.current = irisData;
    }, [irisData, featureShopPageName, triggerAnalyticsData]);

    // Clean up localStorage (non-preview only)
    useEffect(() => {
        if (!isPreview) {
            LocalStorage.removeData('irisEvergreenload', true);
        }
    }, [isPreview]);

    const pageNameAttribute = (data) => {
        setFeatureShopPageName(data);
    };

    const renderMiniPDPComponent = enableMiniPDPFeature ? renderMiniPDP() : null;

    // === Feature Shops (URL-based, takes precedence) ===
    if (isFeatureShopEligible) {
        return (
            <Layout>
                <FeatureShops location={location} pageNameChange={pageNameAttribute} />
                {renderMiniPDPComponent}
            </Layout>
        );
    }

    // === IRIS ===
    return (
        <Layout>
            <IrisPage
                params={params}
                pageType="MARKETING"
                irisFallbackEnabled={irisFallbackEnabled}
                irisComponents={irisComponentsForMarketing}
                irisFallbackPreferences={irisFallbackPreferences}
                redirectAction={RedirectAction.setRedirectUrl}
                aggregateAPI
                location={location}
            />
            {renderMiniPDPComponent}
        </Layout>
    );
}

Marketing.propTypes = {
    actions: PropTypes.objectOf(PropTypes.func),
    irisData: PropTypes.object,
    location: PropTypes.object,
    history: PropTypes.object,
    irisFallbackPreferences: PropTypes.object,
    irisFallbackEnabled: PropTypes.bool,
    disableIrisCall: PropTypes.bool,
    isPreview: PropTypes.bool,
    enableFeatureShops: PropTypes.bool,
    enableMiniPDPFeature: PropTypes.bool,
};

Marketing.defaultProps = {
    actions: {},
    irisData: {},
    location: {},
    history: {},
    irisFallbackPreferences: {},
    irisFallbackEnabled: false,
    disableIrisCall: false,
    isPreview: false,
    enableFeatureShops: false,
    enableMiniPDPFeature: true,
};

const mapStateToProps = ({ irisData, context }) => {
    const featureFlags = get(context, 'featureFlags', {});

    return {
        irisData,
        isPreview: get(context, 'isPreview', false),
        irisFallbackPreferences: get(context, 'preferences.irisfallbackPreferences', {}),
        irisFallbackEnabled: featureFlags.irisFallbackEnabled || false,
        disableIrisCall: featureFlags.disableIrisCall || false,
        enableFeatureShops: featureFlags.enableFeatureShops || false,
        enableMiniPDPFeature: featureFlags.enableMiniPDPFeature !== false,
    };
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        { ...marketingAction, ...IrisActions },
        dispatch
    ),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Marketing));
